import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import {
  Dashboard,
  Add,
  PhotoLibrary,
  CalendarMonth,
  Reviews,
  Analytics,
  Settings,
  Security,
} from '@mui/icons-material';

import { useAppContext } from '../../context/AppContext';
import { MENU_ITEMS } from '../../utils/constants';

// Icon mapping
const iconMap = {
  Dashboard,
  Add,
  PhotoLibrary,
  CalendarMonth,
  Reviews,
  Analytics,
  Settings,
  Security,
};

function Sidebar({ drawerWidth, open, onClose, isMobile }) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { 
    unreadCount,
    realtimeData,
  } = useAppContext();

  // Get notification counts for menu items
  const getNotificationCount = (path) => {
    switch (path) {
      case '/reviews':
        return realtimeData.reviews ? Object.keys(realtimeData.reviews).filter(
          id => realtimeData.reviews[id].requiresResponse
        ).length : 0;
      case '/calendar':
        return realtimeData.posts ? Object.keys(realtimeData.posts).filter(
          id => realtimeData.posts[id].status === 'scheduled'
        ).length : 0;
      default:
        return 0;
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      onClose();
    }
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Brand */}
      <Box
        sx={{
          p: 2,
          pt: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 'bold',
            color: 'primary.main',
            textAlign: 'center',
          }}
        >
          KashLegal
          <br />
          <Typography variant="caption" color="text.secondary">
            GBP Manager
          </Typography>
        </Typography>
      </Box>

      <Divider />

      {/* Navigation Menu */}
      <List sx={{ flexGrow: 1, pt: 2 }}>
        {MENU_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;
          const IconComponent = iconMap[item.icon];
          const notificationCount = getNotificationCount(item.path);

          return (
            <ListItem key={item.path} disablePadding sx={{ px: 2, py: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigate(item.path)}
                selected={isActive}
                sx={{
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText',
                    },
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isActive ? 'inherit' : 'text.secondary',
                  }}
                >
                  {IconComponent && <IconComponent />}
                </ListItemIcon>
                
                <ListItemText
                  primary={item.title}
                  secondary={
                    !isActive ? (
                      <Typography
                        variant="caption"
                        sx={{
                          display: { xs: 'none', lg: 'block' },
                          opacity: 0.7,
                          mt: 0.5,
                        }}
                      >
                        {item.description}
                      </Typography>
                    ) : null
                  }
                  sx={{ my: 0 }}
                />
                
                {notificationCount > 0 && (
                  <Chip
                    size="small"
                    label={notificationCount}
                    color="error"
                    sx={{ ml: 1, minWidth: 'auto' }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* Footer Info */}
      <Box sx={{ p: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
        >
          v1.0.0
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
        >
          © 2024 KashLegal
        </Typography>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

export default Sidebar;